import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, SiteStatus, AdminWebsiteListItem } from '../api';

const STATUS_CONFIG: Record<SiteStatus, { label: string; color: string; bg: string }> = {
  form_incomplete:    { label: 'Form Incomplete', color: 'text-ink-400',    bg: 'bg-ink-700' },
  form_completed:     { label: 'Form Completed',  color: 'text-amber-400',  bg: 'bg-amber-500/20' },
  generating:         { label: 'Generating…',     color: 'text-violet-400', bg: 'bg-violet-500/20' },
  website_in_progress:{ label: 'In Progress',     color: 'text-royal-400',  bg: 'bg-royal-500/20' },
  website_completed:  { label: 'Completed',        color: 'text-emerald-400',bg: 'bg-emerald-500/20' },
};

export default function AdminWebsites() {
  const navigate = useNavigate();
  const [websites, setWebsites] = useState<AdminWebsiteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<SiteStatus | ''>('');

  useEffect(() => {
    loadWebsites();
  }, [filterStatus]);

  const loadWebsites = async () => {
    try {
      setLoading(true);
      const data = await api.getWebsites(filterStatus || undefined);
      setWebsites(data.websites);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load websites');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (influencerDbId: number, newStatus: SiteStatus) => {
    try {
      await api.updateWebsite(influencerDbId, { status: newStatus });
      loadWebsites();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading && websites.length === 0) {
    return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
        </div>
    );
  }

  return (
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold text-ink-50">Website Management</h1>
            <p className="text-ink-400 mt-1">Track and manage influencer website builds</p>
          </div>
        </div>

        {error && (
          <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4 text-accent-400">
            {error}
          </div>
        )}

        {/* Status Filters */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus('')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === ''
                ? 'bg-accent-500/20 text-accent-400'
                : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
            }`}
          >
            All
          </button>
          {(Object.keys(STATUS_CONFIG) as SiteStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === status
                  ? `${STATUS_CONFIG[status].bg} ${STATUS_CONFIG[status].color}`
                  : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
              }`}
            >
              {STATUS_CONFIG[status].label}
            </button>
          ))}
        </div>

        {/* Websites Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-800">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-ink-400 uppercase tracking-wider">
                    Influencer
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-ink-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-ink-400 uppercase tracking-wider">
                    Intake Submitted
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-ink-400 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-ink-400 uppercase tracking-wider">
                    Site Domain
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-ink-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-800/50">
                {websites.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-ink-400">
                      No websites found matching the selected filter.
                    </td>
                  </tr>
                ) : (
                  websites.map((website) => {
                    const statusConfig = STATUS_CONFIG[website.status];
                    return (
                      <tr 
                        key={website.influencer_db_id} 
                        className="hover:bg-ink-800/30 transition-colors cursor-pointer"
                        onClick={() => navigate(`/admin/websites/${website.influencer_db_id}`)}
                      >
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-ink-100">{website.stage_name}</p>
                            <p className="text-sm text-ink-500">{website.email || 'No email'}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {website.status === 'generating' ? (
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-sm font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                              <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
                              {statusConfig.label}
                            </span>
                          ) : (
                            <select
                              value={website.status}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStatusChange(website.influencer_db_id, e.target.value as SiteStatus);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={`px-3 py-1 rounded text-sm font-medium ${statusConfig.bg} ${statusConfig.color} bg-opacity-100 border-0 cursor-pointer`}
                            >
                              {(Object.keys(STATUS_CONFIG) as SiteStatus[]).filter(s => s !== 'generating').map((s) => (
                                <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-ink-300">
                          {formatDate(website.intake_submitted_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-ink-300">
                          {formatDate(website.site_updated_at)}
                        </td>
                        <td className="px-6 py-4">
                          {website.site_domain ? (
                            <a
                              href={website.site_domain}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-sm text-accent-400 hover:text-accent-300 flex items-center gap-1"
                            >
                              {website.site_domain.replace(/^https?:\/\//, '')}
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          ) : (
                            <span className="text-sm text-ink-500">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/websites/${website.influencer_db_id}`);
                            }}
                            className="btn-ghost text-sm"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );
}
